import { Injectable } from "@angular/core";

import { Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BfxBlotterAction,
  OpsIntPromiseActionService,
  OrderDataLookupService,
} from "shared/cubiq-services";

import { TranslateService } from "@ngx-translate/core";

import { BfxBlotterOpsIntContextMenuService } from "./bfx-blotter-ops-int-contextmenu.service";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../../golden-layout/golden-layout-service";
import { BfxConfigService } from "../../../shared/auth-services/system-config/bfx-config.service";

// models
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import {
  BfxBaseBlotterActionService,
  ActionListener,
} from "../../bfx-base-blotter/bfx-base-blotter-action.service";
import { BfxBlotterActionEvent } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { DashboardItemType } from "app/bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "app/golden-layout/golden-layout-addition-mode";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { OpsIntActionResponse } from "shared/cubiq-services/action/IOpsIntActionService";
import { LoggerService } from "@bfx/logger";
import { BfxConfigIDRefHelper } from "../../../shared/helper-services/bfx-config-id-ref-helper";
import { DETAILS_MIN_SIZE } from "shared/cubiq-services/model/golden-layout.constants";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxBlotterOpsIntActionsService extends BfxBaseBlotterActionService<any> {
  private opsIntActionDialogSource = new Subject<BfxDialogData>();

  public dialogCtrl$ = this.opsIntActionDialogSource.asObservable();

  constructor(
    contextMenuService: BfxBlotterOpsIntContextMenuService,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    configService: BfxConfigService,
    protected actionService: OpsIntPromiseActionService,
    private orderDataService: OrderDataLookupService,
  ) {
    super(contextMenuService, layoutService, translationService, configService);
  }

  @ActionListener(BfxBlotterAction.ACTION_OPS_INT_VIEW_DETAILS)
  public onViewDetails(event: BfxBlotterActionEvent) {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.sourceEvent = event.sourceEvent;
    config.popoutResizable = false;
    config.minimumSize = DETAILS_MIN_SIZE;

    config.data = { openTradesTab: false };

    // don't persist selectedIds in config
    Object.defineProperty(config.data, "selectedIds", {
      enumerable: false,
      writable: true,
    });

    if (event.itemState.hasReferencedDealIds) {
      config.ladderBlotter = event.ladderBlotter;
      config.data = { tradeDealId: event.itemState.referencedDealIds[0] };
      config.sourceEvent = event.sourceEvent;

      let title: string = "Trade Details"; // temp, will be replaced in component when deal is loaded

      this.layoutService.launchFromDashboard(
        title,
        DashboardItemType.DealDetails,
        true,
        GoldenLayoutAdditionMode.DEFAULT,
        config,
      );
    } else if (event.itemState.hasReferencedOrderIds) {
      config.data.selectedIds = event.itemState.referencedOrderIds;
      config.ladderBlotter = event.ladderBlotter;

      let orderRef =
        Array.isArray(event.selectedItems) && event.selectedItems.length > 0
          ? BfxConfigIDRefHelper.getOpsIntOrderRef(event.selectedItems[0])
          : null;

      let title: string =
        orderRef == null
          ? this.translationService.instant("order.details.title.noid")
          : this.translationService.instant("order.details.title", [orderRef]);

      this.layoutService.launchFromDashboard(
        title,
        DashboardItemType.OrderDetails,
        true,
        GoldenLayoutAdditionMode.DEFAULT,
        config,
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_OPS_INT_RESEND)
  public onResend(event: BfxBlotterActionEvent) {
    this.actionService.resend(event.itemState.referencedOpsIntIds);
  }

  @ActionListener(BfxBlotterAction.ACTION_OPS_INT_REPRICE)
  public onRePriceAction(event: BfxBlotterActionEvent) {
    let opsIntIds = event.itemState.referencedOpsIntIds;
    this.actionService.reprice(opsIntIds).then((res) => {
      if (res != null && Array.isArray(res)) {
        res.forEach((encodable: OpsIntActionResponse) => {
          if (encodable != null && encodable instanceof CubiqTS.ServiceRequestError) {
            logger.out.error(
              `Error: ${this.constructor.name}.onRePriceAction() ${encodable.errorList}`,
            );
          }
        });
      } else if (res != null) {
        logger.out.error(`Error: ${this.constructor.name}.onRePriceAction(), response : ${res} `);
      } else {
        // nothing is rotten in the land down-under
      }
    });
  }

  @ActionListener(BfxBlotterAction.ACTION_OPS_INT_CLEAR)
  public onClearAction(event: BfxBlotterActionEvent) {
    let isDialogOpening: boolean = event.itemState.clearDialogReferencedIds.size != 0;
    let areOrderIDsPresent = event.itemState.referencedOrderIds.length > 0;
    let isMultiId = areOrderIDsPresent
      ? event.itemState.referencedOrderIds.length > 1
      : event.itemState.referencedDealIds.length > 1;
    let header_title = "";
    let clearDialogReferencedIds: string[] = Array.from(
      event.itemState.clearDialogReferencedIds.values(),
    );
    let refs: string[] = [];
    let multiSelectIdsNotFitForDialog = event.itemState.referencedOpsIntIds.filter(
      (id) => !event.itemState.clearDialogReferencedIds.has(id),
    );
    if (areOrderIDsPresent) {
      header_title = isMultiId
        ? "dialog.ops.int.clear.order.multi.title"
        : "dialog.ops.int.clear.order.single.title";
      refs = (Array.isArray(event.selectedItems) ? event.selectedItems : []).map(
        (ops: CubiqTS.OpsIntEventImpl) => BfxConfigIDRefHelper.getOpsIntOrderRef(ops),
      );
    } else {
      header_title = isMultiId
        ? "dialog.ops.int.clear.deal.multi.title"
        : "dialog.ops.int.clear.deal.single.title";
      refs = (Array.isArray(event.selectedItems) ? event.selectedItems : []).map(
        (ops: CubiqTS.OpsIntEventImpl) => BfxConfigIDRefHelper.getOpsIntDealRef(ops),
      );
    }

    let payload = {
      title: this.translationService.instant(header_title),
      clearDialogReferencedIds: clearDialogReferencedIds,
      refs: refs,
    };
    if (isDialogOpening) {
      this.opsIntActionDialogSource.next(
        new BfxDialogData(BfxDialogCommand.DIALOG_OPS_INT_CONFIRM_CLEAR, null, event, payload),
      );
      // this.actionService.clear(multiSelectIdsNotFitForDialog, "", "", {})
    } else {
      this.actionService.clear(event.itemState.referencedOpsIntIds, "", "", {});
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_OPS_INT_CANCEL_ORDER)
  public onCancelOrder(event: BfxBlotterActionEvent) {
    if (
      event == null ||
      event.itemState == null ||
      Array.isArray(event.itemState.referencedOpsIntIds) === false ||
      event.itemState.referencedOpsIntIds.length === 0
    ) {
      logger.out.warn(
        `${this.constructor.name}.onCancelOrder() ignoring even with no ids: %O`,
        event,
      );
      return;
    }

    let length = event.itemState.referencedOpsIntIds.length;
    let msg: string =
      length == 1 ? "dialog.ops.int.cancel.order.single" : "dialog.ops.int.cancel.order.multi";
    let title: string =
      length == 1
        ? "dialog.ops.int.cancel.order.single.title"
        : "dialog.ops.int.cancel.order.multi.title";

    let payload = {
      messages: [this.translationService.instant(msg, [length])],
      title: title,
      callback: function confirmCancelOrder() {
        this.actionService.cancelOrder(event.itemState.referencedOpsIntIds);
      }.bind(this),
    };
    this.opsIntActionDialogSource.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN, null, event, payload),
    );
  }
}
